import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { UsersService } from '@treasury/domain/users';
import { WireIsoService, WiresService, WireTemplate } from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels } from '@treasury/domain/wires';
import { LabeledList, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-labeled-list';
import '@treasury/presentation/components/tm-status';
import '@treasury/presentation/components/tm-toggle-content';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { SessionStorageService } from '@treasury/utils';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../../components/jhd-status';

export const tagName = 'wire-template-readonly';
@customElement(tagName)
export class WireTemplateReadonly extends TmBaseComponent {
    @property({ type: Object })
    public wireTemplate = new WireTemplate();

    @property({ type: Boolean })
    public showStatus = true;

    @property({ type: Boolean })
    public showCreationDetails = true;

    @property({ type: Boolean })
    public creditorDetail = true;

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private userService: UsersService;

    @InjectProperty()
    declare private readonly wireIsoService: WireIsoService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly sessionStorageService: SessionStorageService;

    @state()
    private createdBy?: string;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    protected render() {
        return html`<div class="px-6 py-4">
            <div class="flex justify-between">
                <div class="text-lg font-medium mb-1">${this.wireTemplate.name}</div>
                <div class="flex flex-col items-start">
                    <tm-badge uppercase>
                        ${this.wireTemplate.isInternational ? 'International' : 'Domestic'}
                    </tm-badge>
                    ${this.wireTemplate.status
                        ? html`<tm-status
                              .status=${this.wireTemplate.status}
                              class="mt-1"
                          ></tm-status>`
                        : nothing}
                </div>
            </div>
            <div>${this.renderDetails()}${this.renderCreationDetails()} ${this.renderAudit()}</div>
        </div>`;
    }

    protected async firstUpdated() {
        this.wireIsoLabels = await this.wireIsoService.getLabels();

        if (this.showCreationDetails && this.wireTemplate.createdBy) {
            const user = await this.userService.getUserDetails(Number(this.wireTemplate.createdBy));
            this.createdBy = user.name.value ?? '';
        }
    }

    private renderAudit() {
        if (!this.wireTemplate.audit) {
            return nothing;
        }
        return html` <tm-toggle-content label="audit" buttonType="secondary">
            <div class="pb-2">
                <div class="pb-2 font-medium">Audit</div>
                <div class="font-normal text-sm">${this.wireTemplate.audit}</div>
            </div>
        </tm-toggle-content>`;
    }

    private renderDetails() {
        const templateAccounts = {
            wireCompany: this.wireTemplate.wireCompany?.name,
            debitAccount: this.wireTemplate.debitAccount?.accountDisplayLabel,
            beneficiary: this.wireTemplate.beneficiary?.name,
        };
        const templateAccountsList = new LabeledList(
            templateAccounts,
            ['wireCompany', 'debitAccount', 'beneficiary'],
            {
                wireCompany: 'Wire Company',
                debitAccount: 'Debit Account',
                beneficiary: `${this.wireIsoLabels.creditor}`,
            },
            { beneficiary: this.creditorDetail ? 'view-creditor-detail' : undefined }
        );

        const templateDetailsList = new LabeledList(
            this.wireTemplate,
            ['purpose', 'referenceBeneficiary', 'notes'],
            {
                purpose: 'Purpose',
                referenceBeneficiary: `${this.wireIsoLabels.endToEndId}`,
                notes: 'Notes',
            }
        );
        return html` 
            <tm-labeled-list
                        class="py-4"
                        .list=${templateAccountsList}
                    ></tm-labeled-list>
            <hr class="border-t border-dashed" />
            <tm-labeled-list
                        class="py-4"
                        .list=${templateDetailsList}
                    ></tm-labeled-list>
            <hr class="border-t border-dashed mb-4" />
        </div>`;
    }

    private renderCreationDetails() {
        if (!this.showCreationDetails) {
            return nothing;
        }
        return html` <jhd-data-field
            label="Created By"
            .value=${this.createdBy ?? ''}
        ></jhd-data-field>`;
    }

    public static get styles() {
        return [
            css`
                .wire-template-status-chip {
                    flex: 1 1 85px;
                    text-align: right;
                    position: relative;
                    bottom: 5px;
                }

                .secondary-field {
                    --jhd-field-label-width: 150px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplateReadonly;
    }
}
